import type { RouteSegment, StreetSegmentPlanningData } from '@/api/main/types.gen'

type HeapEntry = { nodeId: string; dist: number }

/** Binary min-heap by `dist`. Ties keep insertion order irrelevant for correctness. */
class MinDistHeap {
  private readonly items: HeapEntry[] = []

  get size() {
    return this.items.length
  }

  push(entry: HeapEntry) {
    this.items.push(entry)
    this.bubbleUp(this.items.length - 1)
  }

  pop(): HeapEntry | undefined {
    if (this.items.length === 0) return undefined
    const top = this.items[0]!
    const last = this.items.pop()!
    if (this.items.length > 0) {
      this.items[0] = last
      this.bubbleDown(0)
    }
    return top
  }

  private bubbleUp(index: number) {
    while (index > 0) {
      const parent = (index - 1) >> 1
      if (this.items[index]!.dist >= this.items[parent]!.dist) break
      ;[this.items[index], this.items[parent]] = [this.items[parent]!, this.items[index]!]
      index = parent
    }
  }

  private bubbleDown(index: number) {
    for (;;) {
      const left = index * 2 + 1
      const right = left + 1
      let smallest = index
      if (left < this.items.length && this.items[left]!.dist < this.items[smallest]!.dist) {
        smallest = left
      }
      if (right < this.items.length && this.items[right]!.dist < this.items[smallest]!.dist) {
        smallest = right
      }
      if (smallest === index) break
      ;[this.items[index], this.items[smallest]] = [this.items[smallest]!, this.items[index]!]
      index = smallest
    }
  }
}

export type RoutingEdge = { toNode: string; segment: StreetSegmentPlanningData; reverse: boolean }

/** Adjacency map over street segments; build once and reuse across shortest-path queries. */
export type RoutingGraph = Map<string, RoutingEdge[]>

/** Either a prebuilt graph or raw segments (built ad hoc — prefer the prebuilt form in hot paths). */
export type RoutingSource = RoutingGraph | StreetSegmentPlanningData[]

export function buildRoutingGraph(segments: StreetSegmentPlanningData[]): RoutingGraph {
  const adj: RoutingGraph = new Map()

  for (const seg of segments) {
    const addEdge = (from: string, to: string, reverse: boolean) => {
      if (!adj.has(from)) adj.set(from, [])
      adj.get(from)!.push({ toNode: to, segment: seg, reverse })
    }
    addEdge(seg.source_node_id, seg.destination_node_id, false)
    if (seg.is_bidirectional) addEdge(seg.destination_node_id, seg.source_node_id, true)
  }

  return adj
}

function resolveGraph(source: RoutingSource): RoutingGraph {
  return Array.isArray(source) ? buildRoutingGraph(source) : source
}

type DijkstraResult = {
  prev: Map<string, { fromNode: string; segment: StreetSegmentPlanningData; reverse: boolean }>
}

function runDijkstra(
  adj: RoutingGraph,
  fromNodeId: string,
  stopAtNodeId: string | null,
  avoidNodes?: Set<string>,
  avoidExemptNodeId?: string
): DijkstraResult {
  const dist = new Map<string, number>()
  const prev: DijkstraResult['prev'] = new Map()
  const visited = new Set<string>()
  const heap = new MinDistHeap()
  dist.set(fromNodeId, 0)
  heap.push({ nodeId: fromNodeId, dist: 0 })

  while (heap.size > 0) {
    const current = heap.pop()!
    if (visited.has(current.nodeId)) continue
    if (current.dist !== dist.get(current.nodeId)) continue
    if (current.nodeId === stopAtNodeId) break
    visited.add(current.nodeId)

    for (const { toNode, segment, reverse } of adj.get(current.nodeId) ?? []) {
      if (visited.has(toNode)) continue
      if (avoidNodes && avoidNodes.has(toNode) && toNode !== fromNodeId && toNode !== avoidExemptNodeId) {
        continue
      }
      const newDist = current.dist + segment.length
      if (newDist < (dist.get(toNode) ?? Infinity)) {
        dist.set(toNode, newDist)
        prev.set(toNode, { fromNode: current.nodeId, segment, reverse })
        heap.push({ nodeId: toNode, dist: newDist })
      }
    }
  }

  return { prev }
}

function reconstructPath(prev: DijkstraResult['prev'], fromNodeId: string, toNodeId: string): RouteSegment[] | null {
  if (!prev.has(toNodeId)) return null

  const path: RouteSegment[] = []
  let current = toNodeId
  while (current !== fromNodeId) {
    const p = prev.get(current)
    if (!p) return null
    path.unshift({
      street_segment_id: p.segment.street_segment_id,
      entrance_node_id: p.reverse ? p.segment.destination_node_id : p.segment.source_node_id,
      exit_node_id: current,
    })
    current = p.fromNode
  }
  return path
}

/**
 * Dijkstra over street segments by `length`. Bidirectional segments add a reverse edge;
 * `avoidNodes` skips intermediate nodes but never the endpoints (so edit-reconnect can
 * still land on a path node that would otherwise be forbidden).
 */
export function findShortestPath(
  source: RoutingSource,
  fromNodeId: string,
  toNodeId: string,
  avoidNodes?: Set<string>
): RouteSegment[] | null {
  if (fromNodeId === toNodeId) return []
  const { prev } = runDijkstra(resolveGraph(source), fromNodeId, toNodeId, avoidNodes, toNodeId)
  return reconstructPath(prev, fromNodeId, toNodeId)
}

/**
 * Single Dijkstra run from `fromNodeId`, then returns the path to the first entry of
 * `targetNodeIds` (in the given preference order) that is reachable. Targets equal to
 * `fromNodeId` are skipped. Used for edit-mode reconnect hints, where preference order
 * (earliest tail entrance) matters more than pure distance.
 */
export function findShortestPathToFirstReachable(
  source: RoutingSource,
  fromNodeId: string,
  targetNodeIds: string[]
): RouteSegment[] | null {
  const { prev } = runDijkstra(resolveGraph(source), fromNodeId, null)
  for (const target of targetNodeIds) {
    if (target === fromNodeId) continue
    const path = reconstructPath(prev, fromNodeId, target)
    if (path) return path
  }
  return null
}
